import { t } from "ttag";

import type { FunnelPhase } from "../models";
import { FUNNEL_PHASE } from "../models";
import { FUNNEL_PHASES } from "../router/constants";

interface FunnelStepperProps {
  /** Phase of the currently rendered step. */
  currentPhase: FunnelPhase;
}

const PHASE_LABELS: Record<FunnelPhase, string> = {
  [FUNNEL_PHASE.START]: t`Start`,
  [FUNNEL_PHASE.PRELIMINARY]: t`Preliminary`,
  [FUNNEL_PHASE.HEALTH]: t`Health`,
  [FUNNEL_PHASE.DETAILS]: t`Details`,
  [FUNNEL_PHASE.ELIGIBILITY]: t`Eligibility`,
};

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-3.5 text-white"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/**
 * Five-phase progress indicator mirroring the levelsRx consultation stepper:
 * completed phases show a filled navy circle with a check, the active phase a
 * ringed circle with a dot, and upcoming phases an empty outlined circle.
 */
export const FunnelStepper = ({ currentPhase }: FunnelStepperProps) => {
  const currentIndex = FUNNEL_PHASES.indexOf(currentPhase);

  return (
    <ol className="flex items-center">
      {FUNNEL_PHASES.map((phase, index) => {
        const isCompleted = index < currentIndex;
        const isActive = index === currentIndex;

        return (
          <li key={phase} className="flex items-center">
            {index > 0 && (
              <span
                className={`h-px w-4 sm:w-8 ${
                  index <= currentIndex ? "bg-secondary-500" : "bg-neutral-300"
                }`}
                aria-hidden="true"
              />
            )}

            <span className="flex items-center gap-2">
              <span
                className={`flex size-6 items-center justify-center rounded-full border-2 ${
                  isCompleted
                    ? "border-secondary-500 bg-secondary-500"
                    : isActive
                      ? "border-secondary-500 bg-white"
                      : "border-neutral-300 bg-white"
                }`}
              >
                {isCompleted ? (
                  <CheckIcon />
                ) : isActive ? (
                  <span className="size-2.5 rounded-full bg-secondary-500" />
                ) : null}
              </span>

              <span
                className={`hidden text-sm font-bold sm:inline ${
                  isActive || isCompleted
                    ? "text-primary-950"
                    : "text-neutral-700"
                }`}
              >
                {PHASE_LABELS[phase]}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
};
